import {
  defaultFacelessImageStylePresetId,
  defaultFacelessMotionVideoModelId,
  defaultFacelessMotionVideoResolution,
  defaultMusicTrackId,
  defaultTtsModelId,
  defaultTtsVoiceId,
  facelessImageStylePresets,
  facelessScriptGenerationLimits,
  getFacelessMotionVideoModelOption,
  isEnabledTtsSelection,
  isFacelessMotionVideoModelId,
  isFacelessMotionVideoResolution,
  parseSceneDensity,
  sceneDensityLimits,
  type FacelessVideoCreationSettings,
  type RedditCommentsCreationSettings,
  type TextMessagesCreationSettings,
  type TtsModelId,
} from "@reelmagic/domain";
import { playbackSpeedLimits } from "@reelmagic/remotion";
import { resolveSupportedMusicTrackId } from "./music-track-selection";

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function resolvePlaybackSpeed(value: unknown) {
  return typeof value === "number" &&
    Number.isFinite(value) &&
    value >= playbackSpeedLimits.min &&
    value <= playbackSpeedLimits.max
    ? Math.round(value * 10) / 10
    : playbackSpeedLimits.default;
}

function resolveTtsSelection(
  modelId: unknown,
  voiceId: unknown,
  fallback: { modelId: TtsModelId; voiceId: string },
) {
  if (
    isEnabledTtsSelection({
      modelId,
      voiceId,
    })
  ) {
    return {
      modelId: modelId as TtsModelId,
      voiceId: voiceId as string,
    };
  }

  return fallback;
}

export const defaultFacelessCreationSettings: FacelessVideoCreationSettings = {
  imageStylePresetId: defaultFacelessImageStylePresetId,
  motionVideoModelId: defaultFacelessMotionVideoModelId,
  motionVideoResolution: defaultFacelessMotionVideoResolution,
  musicTrackId: defaultMusicTrackId,
  playbackSpeed: playbackSpeedLimits.default,
  sceneDensity: sceneDensityLimits.defaultSecondsPerScene,
  scriptTargetDurationSeconds:
    facelessScriptGenerationLimits.defaultTargetDurationSeconds,
  ttsModelId: defaultTtsModelId,
  visualMode: "image",
  voiceId: defaultTtsVoiceId,
};

export const defaultRedditCommentsCreationSettings: RedditCommentsCreationSettings = {
  musicTrackId: defaultMusicTrackId,
  playbackSpeed: playbackSpeedLimits.default,
  ttsModelId: defaultTtsModelId,
  voiceId: defaultTtsVoiceId,
};

export const defaultTextMessagesCreationSettings: TextMessagesCreationSettings = {
  contactTtsModelId: defaultTtsModelId,
  contactVoiceId: defaultTtsVoiceId,
  musicTrackId: defaultMusicTrackId,
  playbackSpeed: playbackSpeedLimits.default,
  senderTtsModelId: defaultTtsModelId,
  senderVoiceId: defaultTtsVoiceId,
};

export function resolveFacelessCreationSettings(
  value: unknown,
  legacyMusicTrackId?: string,
): FacelessVideoCreationSettings {
  const input = readRecord(value);
  const tts = resolveTtsSelection(input.ttsModelId, input.voiceId, {
    modelId: defaultFacelessCreationSettings.ttsModelId,
    voiceId: defaultFacelessCreationSettings.voiceId,
  });
  const motionVideoModelId = isFacelessMotionVideoModelId(
    input.motionVideoModelId,
  )
    ? input.motionVideoModelId
    : defaultFacelessCreationSettings.motionVideoModelId;
  const requestedResolution = isFacelessMotionVideoResolution(
    input.motionVideoResolution,
  )
    ? input.motionVideoResolution
    : defaultFacelessCreationSettings.motionVideoResolution;
  const supportedResolutions =
    getFacelessMotionVideoModelOption(motionVideoModelId)
      ?.supportedResolutions ?? [];
  const motionVideoResolution = supportedResolutions.includes(
    requestedResolution,
  )
    ? requestedResolution
    : supportedResolutions.includes(
          defaultFacelessCreationSettings.motionVideoResolution,
        )
      ? defaultFacelessCreationSettings.motionVideoResolution
      : (supportedResolutions[0] ??
        defaultFacelessCreationSettings.motionVideoResolution);
  const targetDurationSeconds = input.scriptTargetDurationSeconds;

  return {
    imageStylePresetId: facelessImageStylePresets.some(
      (preset) => preset.id === input.imageStylePresetId,
    )
      ? (input.imageStylePresetId as FacelessVideoCreationSettings["imageStylePresetId"])
      : defaultFacelessCreationSettings.imageStylePresetId,
    motionVideoModelId,
    motionVideoResolution,
    musicTrackId: resolveSupportedMusicTrackId(
      typeof input.musicTrackId === "string"
        ? input.musicTrackId
        : legacyMusicTrackId,
    ),
    playbackSpeed: resolvePlaybackSpeed(input.playbackSpeed),
    sceneDensity:
      parseSceneDensity(input.sceneDensity) ??
      defaultFacelessCreationSettings.sceneDensity,
    scriptTargetDurationSeconds:
      typeof targetDurationSeconds === "number" &&
      Number.isInteger(targetDurationSeconds) &&
      targetDurationSeconds >=
        facelessScriptGenerationLimits.minTargetDurationSeconds &&
      targetDurationSeconds <=
        facelessScriptGenerationLimits.maxTargetDurationSeconds &&
      targetDurationSeconds % 5 === 0
        ? targetDurationSeconds
        : defaultFacelessCreationSettings.scriptTargetDurationSeconds,
    ttsModelId: tts.modelId,
    visualMode:
      input.visualMode === "motion" || input.visualMode === "image"
        ? input.visualMode
        : defaultFacelessCreationSettings.visualMode,
    voiceId: tts.voiceId,
  };
}

export function resolveRedditCommentsCreationSettings(
  value: unknown,
  legacyMusicTrackId?: string,
): RedditCommentsCreationSettings {
  const input = readRecord(value);
  const tts = resolveTtsSelection(input.ttsModelId, input.voiceId, {
    modelId: defaultRedditCommentsCreationSettings.ttsModelId,
    voiceId: defaultRedditCommentsCreationSettings.voiceId,
  });

  return {
    musicTrackId: resolveSupportedMusicTrackId(
      typeof input.musicTrackId === "string"
        ? input.musicTrackId
        : legacyMusicTrackId,
    ),
    playbackSpeed: resolvePlaybackSpeed(input.playbackSpeed),
    ttsModelId: tts.modelId,
    voiceId: tts.voiceId,
  };
}

export function resolveTextMessagesCreationSettings(
  value: unknown,
  legacyMusicTrackId?: string,
): TextMessagesCreationSettings {
  const input = readRecord(value);
  const senderTts = resolveTtsSelection(
    input.senderTtsModelId,
    input.senderVoiceId,
    {
      modelId: defaultTextMessagesCreationSettings.senderTtsModelId,
      voiceId: defaultTextMessagesCreationSettings.senderVoiceId,
    },
  );
  const contactTts = resolveTtsSelection(
    input.contactTtsModelId,
    input.contactVoiceId,
    {
      modelId: defaultTextMessagesCreationSettings.contactTtsModelId,
      voiceId: defaultTextMessagesCreationSettings.contactVoiceId,
    },
  );

  return {
    contactTtsModelId: contactTts.modelId,
    contactVoiceId: contactTts.voiceId,
    musicTrackId: resolveSupportedMusicTrackId(
      typeof input.musicTrackId === "string"
        ? input.musicTrackId
        : legacyMusicTrackId,
    ),
    playbackSpeed: resolvePlaybackSpeed(input.playbackSpeed),
    senderTtsModelId: senderTts.modelId,
    senderVoiceId: senderTts.voiceId,
  };
}
